import React, { useState } from "react";
import {
  Brain,
  Users,
  Search,
  Filter,
  MoreVertical,
  CheckCircle2,
  Clock,
  AlertCircle,
  Globe,
  FileCheck,
  Loader2,
  FileText,
  Eye
} from "lucide-react";
import DemoSidebar from './DemoSidebar';

// Sample student data
const SAMPLE_STUDENTS = [
  {
    id: 1,
    name: "Alice Johnson",
    email: "alice.j@school.edu",
    grade: "A",
    attendance: "95%",
    lastSubmission: "2024-11-10",
    homeworkStatus: "Pending Check",
    performanceStatus: "Excellent",
    subjects: ["Calculus", "Physics"],
    homework: {
      submitted: true,
      file: "Calculus_HW1.pdf",
      score: null,
      feedback: null,
      checkedAt: null
    }
  },
  {
    id: 2,
    name: "Bob Smith",
    email: "bob.s@school.edu",
    grade: "B+",
    attendance: "88%",
    lastSubmission: "2024-11-09",
    homeworkStatus: "Pending Check",
    performanceStatus: "Good",
    subjects: ["Calculus", "Chemistry"],
    homework: {
      submitted: true,
      file: "Calculus_HW2.pdf",
      score: null,
      feedback: null,
      checkedAt: null
    }
  },
  {
    id: 3,
    name: "Carol White",
    email: "carol.w@school.edu",
    grade: "A-",
    attendance: "92%",
    lastSubmission: "2024-11-08",
    homeworkStatus: "Pending Check",
    performanceStatus: "Very Good",
    subjects: ["Biology", "Calculus"],
    homework: {
      submitted: true,
      file: "Calculus_HW3.pdf",
      score: null,
      feedback: null,
      checkedAt: null
    }
  }
];

// Simulated AI responses
const AI_RESPONSES = {
  "Calculus_HW1.pdf": {
    score: 95,
    feedback: "Excellent work on derivatives. Clear understanding of chain rule application. Minor error in integration limits.",
  },
  "Calculus_HW2.pdf": {
    score: 82,
    feedback: "Good grasp of basic concepts. Need more practice with implicit differentiation. Show more steps in solutions.",
  },
  "Calculus_HW3.pdf": {
    score: 88,
    feedback: "Very good work overall. Strong on derivatives, but watch for sign errors in integration by parts.",
  }
};

const StudentsPage = () => {
  const [students, setStudents] = useState(SAMPLE_STUDENTS);
  const [selectedFilter, setSelectedFilter] = useState("All Students");
  const [searchTerm, setSearchTerm] = useState("");
  const [checking, setChecking] = useState({});
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const simulateAIGrading = (studentId, file) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(AI_RESPONSES[file]);
      }, 2000);
    });
  };

  const handleCheckHomework = async (studentId) => {
    setChecking((prev) => ({ ...prev, [studentId]: true }));
    const student = students.find((s) => s.id === studentId);
    
    try {
      const result = await simulateAIGrading(studentId, student.homework.file);
      const currentTime = new Date().toLocaleString();
      
      const updatedStudent = {
        ...student,
        homeworkStatus: "Completed",
        homework: {
          ...student.homework,
          score: result.score,
          feedback: result.feedback,
          checkedAt: currentTime
        }
      };

      setStudents((prev) => prev.map((s) => 
        s.id === studentId ? updatedStudent : s
      ));
      
      setSelectedStudent({
        name: student.name,
        file: student.homework.file,
        score: result.score,
        feedback: result.feedback,
        checkedAt: currentTime
      });
      setShowGradeModal(true);
    } catch (error) {
      console.error("Error grading homework:", error);
    } finally {
      setChecking((prev) => ({ ...prev, [studentId]: false }));
    }
  };

  const handleViewGrade = (student) => {
    setSelectedStudent({
      name: student.name,
      file: student.homework.file,
      score: student.homework.score,
      feedback: student.homework.feedback,
      checkedAt: student.homework.checkedAt
    });
    setShowGradeModal(true);
  };

  const handleCheckAll = async () => {
    const pendingStudents = students.filter((s) => s.homeworkStatus === "Pending Check");
    for (const student of pendingStudents) {
      await handleCheckHomework(student.id);
    }
  };
  const renderTableHeader = () => {
    const headers = [
      "Student Info", "Grade", "Attendance", "Homework Status",
      "Performance", "Subjects", "Actions"
    ];

    return React.createElement('thead', {
      className: 'bg-gray-50'
    }, 
      React.createElement('tr', null, 
        headers.map((header, index) => 
          React.createElement('th', {
            key: header,
            className: `px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
              header === 'Actions' ? 'text-right' : ''
            }`
          }, header)
        )
      )
    );
  };

  const renderStudentCell = (student) => {
    return React.createElement('td', {
      className: 'px-6 py-4'
    },
      React.createElement('div', {
        className: 'flex items-center'
      }, [
        React.createElement('div', {
          key: 'avatar',
          className: 'flex-shrink-0 h-10 w-10'
        },
          React.createElement('div', {
            className: 'h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center'
          },
            React.createElement('span', {
              className: 'text-gray-600 font-medium'
            }, student.name.charAt(0))
          )
        ),
        React.createElement('div', {
          key: 'info',
          className: 'ml-4'
        }, [
          React.createElement('div', {
            key: 'name',
            className: 'text-sm font-medium text-gray-900'
          }, student.name),
          React.createElement('div', {
            key: 'email',
            className: 'text-sm text-gray-500'
          }, student.email)
        ])
      ])
    );
  };

  const renderHomeworkStatus = (student) => {
    return React.createElement('td', {
      className: 'px-6 py-4'
    },
      React.createElement('div', {
        className: 'flex items-center justify-between'
      }, [
        React.createElement('div', {
          className: 'flex items-center space-x-2',
          key: 'status-container'
        }, [
          React.createElement('span', {
            key: 'status',
            className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              student.homeworkStatus === "Completed"
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`
          }, student.homeworkStatus === "Completed" 
             ? `${student.homework.score}%` 
             : student.homeworkStatus),
          student.homeworkStatus === "Completed" && 
            React.createElement('button', {
              key: 'view-button',
              onClick: () => handleViewGrade(student),
              className: 'ml-2 text-green-600 hover:text-green-700 transition-colors'
            }, [
              React.createElement(Eye, {
                className: 'w-4 h-4'
              })
            ])
        ]),
        student.homeworkStatus === "Pending Check" &&
          React.createElement('button', {
            key: 'check-button',
            onClick: () => handleCheckHomework(student.id),
            disabled: checking[student.id],
            className: 'ml-2 text-blue-600 hover:text-blue-700 text-sm flex items-center'
          }, [
            React.createElement(checking[student.id] ? Loader2 : FileCheck, {
              key: 'icon',
              className: `w-4 h-4 ${checking[student.id] ? 'animate-spin' : ''}`
            }),
            React.createElement('span', {
              key: 'text',
              className: 'ml-1'
            }, 'Check')
          ])
      ])
    );
  };

  const renderPerformanceCell = (student) => {
    const getIcon = () => {
      switch (student.performanceStatus) {
        case "Excellent":
          return React.createElement(CheckCircle2, {
            className: 'w-4 h-4 text-green-500'
          });
        case "Very Good":
          return React.createElement(CheckCircle2, {
            className: 'w-4 h-4 text-blue-500'
          });
        default:
          return React.createElement(Clock, {
            className: 'w-4 h-4 text-yellow-500'
          });
      }
    };

    return React.createElement('td', {
      className: 'px-6 py-4'
    },
      React.createElement('div', {
        className: 'flex items-center'
      }, [
        getIcon(),
        React.createElement('span', {
          className: 'ml-2 text-sm'
        }, student.performanceStatus)
      ])
    );
  };

  const renderSubjectsCell = (student) => {
    return React.createElement('td', {
      className: 'px-6 py-4'
    },
      React.createElement('div', {
        className: 'flex flex-wrap gap-1'
      }, 
        student.subjects.map((subject, index) =>
          React.createElement('span', {
            key: index,
            className: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'
          }, subject)
        )
      )
    );
  };

  const renderTableBody = () => {
    return React.createElement('tbody', {
      className: 'bg-white divide-y divide-gray-200'
    }, 
      students.map((student) =>
        React.createElement('tr', {
          key: student.id,
          className: 'hover:bg-gray-50'
        }, [
          renderStudentCell(student),
          React.createElement('td', {
            className: 'px-6 py-4'
          }, 
            React.createElement('span', {
              className: 'text-sm'
            }, student.grade)
          ),
          React.createElement('td', {
            className: 'px-6 py-4'
          }, 
            React.createElement('span', {
              className: 'text-sm'
            }, student.attendance)
          ),
          renderHomeworkStatus(student),
          renderPerformanceCell(student),
          renderSubjectsCell(student),
          React.createElement('td', {
            className: 'px-6 py-4 text-right'
          },
            React.createElement('button', {
              className: 'text-gray-400 hover:text-gray-500'
            },
              React.createElement(MoreVertical, {
                className: 'w-5 h-5'
              })
            )
          )
        ])
      )
    );
  };

  const renderTable = () => {
    return React.createElement('div', {
      className: 'bg-white rounded-xl shadow-sm overflow-hidden'
    },
      React.createElement('table', {
        className: 'min-w-full divide-y divide-gray-200'
      }, [
        renderTableHeader(),
        renderTableBody()
      ])
    );
  };

  const GradeModal = ({ student, onClose }) => {
    return React.createElement('div', {
      className: 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'
    }, 
      React.createElement('div', {
        className: 'bg-white rounded-xl p-6 max-w-md w-full mx-4'
      }, [
        React.createElement('div', {
          className: 'flex justify-between items-start mb-4',
          key: 'header'
        }, [
          React.createElement('div', {
            key: 'title'
          }, [
            React.createElement('h3', {
              className: 'text-lg font-semibold text-gray-900',
              key: 'name'
            }, 'Homework Graded'),
            React.createElement('p', {
              className: 'text-sm text-gray-500',
              key: 'student-name'
            }, student.name),
            student.checkedAt && React.createElement('p', {
              className: 'text-xs text-gray-400 mt-1',
              key: 'timestamp'
            }, `Checked on ${student.checkedAt}`)
          ]),
          React.createElement('div', {
            className: 'flex items-center space-x-2',
            key: 'score-container'
          }, [
            React.createElement(CheckCircle2, {
              className: 'w-5 h-5 text-green-500',
              key: 'check-icon'
            }),
            React.createElement('div', {
              className: 'text-3xl font-bold text-gray-900',
              key: 'score'
            }, `${student.score}%`)
          ])
        ]),
        React.createElement('div', {
          className: 'mb-4',
          key: 'file-info'
        }, [
          React.createElement('div', {
            className: 'text-sm font-medium text-gray-700 mb-1',
            key: 'file-label'
          }, 'File'),
          React.createElement('div', {
            className: 'flex items-center text-sm text-gray-600',
            key: 'file-name'
          }, [
            React.createElement(FileText, {
              className: 'w-4 h-4 mr-2',
              key: 'file-icon'
            }),
            student.file
          ])
        ]),
        React.createElement('div', {
          className: 'mb-6',
          key: 'feedback'
        }, [
          React.createElement('div', {
            className: 'text-sm font-medium text-gray-700 mb-1',
            key: 'feedback-label'
          }, 'AI Feedback'),
          React.createElement('p', {
            className: 'text-sm text-gray-600 bg-gray-50 rounded-lg p-3',
            key: 'feedback-content'
          }, student.feedback)
        ]),
        React.createElement('button', {
          className: 'w-full bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors',
          onClick: onClose,
          key: 'close-button'
        }, 'Close')
      ])
    );
  };

  return React.createElement('div', {
    className: 'flex h-screen bg-gray-50'
  }, [
    React.createElement(DemoSidebar, { key: 'sidebar' }),
    React.createElement('div', {
      className: 'flex-1 flex flex-col',
      key: 'main-content'
    }, [
      // Header
      React.createElement('div', {
        className: 'w-full border-b bg-white',
        key: 'header'
      },
        React.createElement('div', {
          className: 'flex items-center justify-between px-6 py-3'
        }, [
          React.createElement('h1', {
            className: 'text-lg font-medium text-gray-900',
            key: 'title'
          }, 'Students'),
          React.createElement('div', {
            className: 'flex items-center space-x-4',
            key: 'actions'
          }, [
            React.createElement('div', {
              className: 'flex items-center space-x-2 cursor-pointer',
              key: 'language'
            }, [
              React.createElement(Globe, {
                className: 'w-4 h-4 text-gray-600'
              }),
              React.createElement('span', {
                className: 'text-sm text-gray-700'
              }, 'English')
            ]),
            React.createElement('div', {
              className: 'flex items-center space-x-2 cursor-pointer',
              key: 'user'
            }, [
              React.createElement('div', {
                className: 'w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center'
              },
                React.createElement(Users, {
                  className: 'w-4 h-4 text-gray-600'
                })
              ),
              React.createElement('span', {
                className: 'text-sm text-gray-700'
              }, 'Admin')
            ])
          ])
        ])
      ),
      // Main Content Area
      React.createElement('div', {
        className: 'flex-1 p-6',
        key: 'content'
      }, [
        // Action Bar
        React.createElement('div', {
          className: 'mb-6 flex items-center justify-between',
          key: 'action-bar'
        }, [
          React.createElement('div', {
            className: 'flex items-center space-x-3',
            key: 'right-actions'
          }, [
            React.createElement('button', {
              className: 'bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2',
              onClick: handleCheckAll,
              key: 'check-all'
            }, [
              React.createElement(FileCheck, {
                className: 'w-4 h-4 mr-2',
                key: 'check-icon'
              }),
              'Check All Homework'
            ])
          ])
        ]),
        // Table
        renderTable(),
        // Grade Modal
        showGradeModal && selectedStudent && 
          React.createElement(GradeModal, {
            student: selectedStudent,
            onClose: () => setShowGradeModal(false),
            key: 'grade-modal'
          })
      ])
    ])
  ]);
};

export default StudentsPage;